import { AlertBanner, Button, Modal, Select, Textarea } from '@helix-ds/react'
import * as Sentry from '@sentry/react'
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/hooks/useAuth'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import {
  submitSupportRequest,
  type SupportRequestResult,
  type SupportRequestSubject,
} from '../api/supportRequestClient'
import { SUPPORT_PHONE } from '../constants'

type SupportRequestModalProps = {
  isOpen: boolean
  onClose: () => void
}

type SupportSubjectOption = {
  value: SupportRequestSubject
  messageId: string
  defaultMessage: string
}

const SUPPORT_SUBJECT_OPTIONS: SupportSubjectOption[] = [
  {
    value: 'help_with_my_account',
    messageId: 'home.support.form.subject.options.helpWithMyAccount',
    defaultMessage: 'Help with my account',
  },
  {
    value: 'cancel_a_test',
    messageId: 'home.support.form.subject.options.cancelATest',
    defaultMessage: 'Cancel a test',
  },
  {
    value: 'questions_about_consent',
    messageId: 'home.support.form.subject.options.questionsAboutConsent',
    defaultMessage: 'Questions about consent',
  },
  {
    value: 'request_a_copy_of_my_report',
    messageId: 'home.support.form.subject.options.requestCopyOfReport',
    defaultMessage: 'Request a copy of my report',
  },
  {
    value: 'where_are_my_results',
    messageId: 'home.support.form.subject.options.whereAreMyResults',
    defaultMessage: 'Where are my results?',
  },
  {
    value: 'something_else',
    messageId: 'home.support.form.subject.options.somethingElse',
    defaultMessage: 'Something else',
  },
]

const SUPPORT_DESCRIPTION_MAX_LENGTH = 5000

const supportLinkClassName = 'text-brand-700 dark:text-brand-300 font-semibold underline-offset-2 hover:underline'

const SupportRequestModal: React.FC<SupportRequestModalProps> = ({ isOpen, onClose }) => {
  const intl = useIntl()
  const { user } = useAuth()
  const apiClient = useFamilyPortalApiClient()
  const [subject, setSubject] = useState<SupportRequestSubject | ''>('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<SupportRequestResult | null>(null)

  const isFormValid = Boolean(subject && description.trim())
  const confirmationEmail = user?.email ?? ''

  function formatSupportPhoneDisplay(phone: string): string {
    const digits = phone.replace(/\D/g, '')
    const nationalNumber = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits

    if (nationalNumber.length === 10) {
      return `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`
    }

    return phone.replace(/^\+/, '')
  }

  function resetAndClose() {
    if (submitting) return
    setSubject('')
    setDescription('')
    setError(null)
    setResult(null)
    onClose()
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmedDescription = description.trim()
    if (!subject || !trimmedDescription || submitting) return

    setSubmitting(true)
    setError(null)
    try {
      const response = await submitSupportRequest(apiClient, {
        subject,
        description: trimmedDescription,
      })
      setResult(response)
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: 'home', action: 'submit-support-request' } })
      setError(
        intl.formatMessage({
          id: 'home.support.form.error.submit',
          defaultMessage: "We couldn't send your request. Please try again.",
        })
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={intl.formatMessage({ id: 'home.support.form.title', defaultMessage: 'Contact support' })}
      size="lg"
    >
      {result?.submitted ? (
        <div className="flex min-h-[248px] flex-col">
          <h3 className="text-text-heading mb-3 text-base font-semibold">
            <FormattedMessage id="home.support.form.success.heading" defaultMessage="We got your message" />
          </h3>
          {/* AlertBanner wraps children in a <p>, so the two lines are block spans, not paragraphs. */}
          <AlertBanner variant="success">
            <span className="block">
              <FormattedMessage
                id="home.support.form.success.body"
                defaultMessage="Our team will reply to {email} as soon as possible. If you don't see it, check your spam folder."
                values={{ email: confirmationEmail }}
              />
            </span>
            <span className="mt-2 block">
              <FormattedMessage
                id="home.support.form.success.profilePrompt"
                defaultMessage="Not the right email? <profileLink>Update it in your profile.</profileLink>"
                values={{
                  profileLink: (chunks) => (
                    <Link to="/profile" className={supportLinkClassName}>
                      {chunks}
                    </Link>
                  ),
                }}
              />
            </span>
          </AlertBanner>
          <div className="border-border-base mt-auto flex justify-end border-t pt-4">
            <Button type="button" onClick={resetAndClose}>
              <FormattedMessage id="home.support.form.done" defaultMessage="Done" />
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            id="support-subject"
            required
            label={intl.formatMessage({
              id: 'home.support.form.subject.label',
              defaultMessage: 'What can we help with?',
            })}
            value={subject}
            onChange={(event) => setSubject(event.target.value as SupportRequestSubject | '')}
            placeholder={intl.formatMessage({
              id: 'home.support.form.subject.placeholder',
              defaultMessage: 'Select a topic',
            })}
            options={SUPPORT_SUBJECT_OPTIONS.map((option) => ({
              value: option.value,
              label: intl.formatMessage({ id: option.messageId, defaultMessage: option.defaultMessage }),
            }))}
          />

          <Textarea
            id="support-description"
            required
            label={intl.formatMessage({
              id: 'home.support.form.description.label',
              defaultMessage: 'How can we help?',
            })}
            rows={7}
            maxLength={SUPPORT_DESCRIPTION_MAX_LENGTH}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={intl.formatMessage({
              id: 'home.support.form.description.placeholder',
              defaultMessage: 'Tell us a little more about what you need.',
            })}
          />

          {error && <AlertBanner variant="error">{error}</AlertBanner>}

          <div className="border-border-base text-text-body border-t pt-4 text-sm">
            <FormattedMessage id="home.support.form.phonePrompt" defaultMessage="Prefer to call?" />{' '}
            <a href={`tel:${SUPPORT_PHONE}`} className={supportLinkClassName}>
              {formatSupportPhoneDisplay(SUPPORT_PHONE)}
            </a>
          </div>

          <div className="border-border-base flex flex-col-reverse gap-3 border-t pt-4 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={resetAndClose} disabled={submitting}>
              <FormattedMessage id="home.support.form.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              type="submit"
              className="min-w-24"
              disabled={!isFormValid}
              loading={submitting}
              aria-busy={submitting}
            >
              {submitting
                ? intl.formatMessage({ id: 'home.support.form.sending', defaultMessage: 'Sending' })
                : intl.formatMessage({ id: 'home.support.form.send', defaultMessage: 'Send' })}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  )
}

export default SupportRequestModal
